import { CurriculumCountryCodes, CurriculumCountryNames } from './curriculum.const'

/** A catch-all pick, kept distinct from "never chose" (null). */
export const US_OTHER_STATES_CODE = 'OTHER'

/** Curriculums under the USA group rather than top-level rows. */
export const US_GROUPED_COUNTRY_CODES: string[] = [CurriculumCountryCodes.USA, CurriculumCountryCodes.FLORIDA]

export interface UsCurriculumState {
  label: string
  /** Must be unique: the row highlight, trigger label and `<option value>` all key off it. */
  curriculumName?: string
  curriculumCountryCode?: CurriculumCountryCodes
  /** Matches geo `region`; `OTHER` on the catch-all. */
  stateCode: string
}

// No backend entry needed: the name is display-only, the API only ever hears the country code.
function commonCoreState(name: string, stateCode: string): UsCurriculumState {
  return { curriculumCountryCode: CurriculumCountryCodes.USA, curriculumName: name, label: name, stateCode }
}

/** Every US state we do not serve a standard for yet. */
export const US_OTHER_STATE: UsCurriculumState = {
  curriculumCountryCode: CurriculumCountryCodes.USA,
  curriculumName: CurriculumCountryNames.USA,
  label: 'All other U.S states',
  stateCode: US_OTHER_STATES_CODE,
}

/** Alphabetical, catch-all pinned last. */
export const US_CURRICULUM_STATES: UsCurriculumState[] = [
  {
    curriculumCountryCode: CurriculumCountryCodes.USA,
    curriculumName: CurriculumCountryNames.CALIFORNIA_CCSSM,
    label: 'California - CA CCSSM',
    stateCode: 'CA',
  },
  commonCoreState('Colorado', 'CO'),
  commonCoreState('Connecticut', 'CT'),
  commonCoreState('Delaware', 'DE'),
  {
    curriculumCountryCode: CurriculumCountryCodes.FLORIDA,
    curriculumName: CurriculumCountryNames.FLORIDA,
    label: 'Florida - B.E.S.T.',
    stateCode: 'FL',
  },
  commonCoreState('Hawaii', 'HI'),
  commonCoreState('Idaho', 'ID'),
  commonCoreState('Illinois', 'IL'),
  commonCoreState('Iowa', 'IA'),
  commonCoreState('Kansas', 'KS'),
  commonCoreState('Maine', 'ME'),
  commonCoreState('Michigan', 'MI'),
  commonCoreState('Montana', 'MT'),
  commonCoreState('Nevada', 'NV'),
  commonCoreState('New Hampshire', 'NH'),
  commonCoreState('New Mexico', 'NM'),
  commonCoreState('Ohio', 'OH'),
  commonCoreState('Oregon', 'OR'),
  commonCoreState('Rhode Island', 'RI'),
  commonCoreState('South Dakota', 'SD'),
  commonCoreState('Vermont', 'VT'),
  commonCoreState('Washington', 'WA'),
  commonCoreState('Wisconsin', 'WI'),
  commonCoreState('Wyoming', 'WY'),
  US_OTHER_STATE,
]

/** Keyed off the code, so Florida vanishes when hom-api drops `fl`. */
export function isUsCurriculumStateAvailable(state: UsCurriculumState, availableCountryCodes: Set<string>): boolean {
  return Boolean(state.curriculumCountryCode) && availableCountryCodes.has(state.curriculumCountryCode as string)
}

/** Rows to render; a standard the backend drops disappears entirely. */
export function getOrderedUsCurriculumStates(availableCountryCodes: Set<string>): UsCurriculumState[] {
  return US_CURRICULUM_STATES.filter((state) => isUsCurriculumStateAvailable(state, availableCountryCodes))
}

export function findUsCurriculumStateByName(curriculumName: string | undefined): UsCurriculumState | undefined {
  if (!curriculumName) return undefined
  return US_CURRICULUM_STATES.find((state) => state.curriculumName === curriculumName)
}

export function findUsCurriculumStateByStateCode(stateCode: string | undefined): UsCurriculumState | undefined {
  if (!stateCode) return undefined
  return US_CURRICULUM_STATES.find((state) => state.stateCode === stateCode.toUpperCase())
}

/** Undefined for non-US curriculums. */
export function getStateCodeForCurriculumName(curriculumName: string | undefined): string | undefined {
  return findUsCurriculumStateByName(curriculumName)?.stateCode
}

/** Overrides the IP guess saved at register. Catch-all excluded: `USA` is also a top-level row. */
export function getPreSignupUsState(curriculumName: string | undefined): UsCurriculumState | undefined {
  const state = findUsCurriculumStateByName(curriculumName)
  return state?.stateCode === US_OTHER_STATES_CODE ? undefined : state
}

/** Undefined for non-US curriculums, so callers fall back to the backend lookup. */
export function getUsCurriculumStateCountryCode(
  curriculumName: string | undefined,
): CurriculumCountryCodes | undefined {
  return findUsCurriculumStateByName(curriculumName)?.curriculumCountryCode
}

/** Every `us` row is one curriculum, so switching between them only relabels: no modal, no grade reset. */
export function isDisplayOnlyCurriculumChange(
  curriculumName: string | undefined,
  usersCurriculumCountryCode: string | undefined,
): boolean {
  const targetCountryCode = getUsCurriculumStateCountryCode(curriculumName)
  return Boolean(targetCountryCode) && targetCountryCode === usersCurriculumCountryCode
}

/** The designed US label, else the backend name as-is (Norge, Українська, ...). */
export function getCurriculumDisplayLabel(curriculumName: string): string {
  return findUsCurriculumStateByName(curriculumName)?.label ?? curriculumName
}
